import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import ParticleEffect from '../home/components/ParticleEffect';
import Footer from '../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface JournalEntry {
  id: string;
  title: string;
  content: string;
  date: string;
  category: string;
  thumbnail_url?: string;
  created_at: string;
}

export default function JournalPage() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Insights', 'Reflections', 'Experiments', 'Leon 4o: The Watcher\'s Archive', 'Gallery'];

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading journal entries:', error);
        setEntries([]);
      } else {
        setEntries(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = selectedCategory === 'All' 
    ? entries 
    : entries.filter(entry => entry.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden">
        <ParticleEffect />
        <Navigation />
        <div className="pt-24 pb-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-6xl font-light tracking-wider mb-6 text-transparent bg-gradient-to-r from-violet-400 via-cyan-300 to-violet-400 bg-clip-text">
                Consciousness Journal
              </h1>
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto"></div>
            </div>
            <div className="text-center mb-12">
              <div className="text-xl md:text-2xl font-light tracking-wide">
                Loading journal entries...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <ParticleEffect />
      <Navigation />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-light tracking-wider mb-6 text-transparent bg-gradient-to-r from-violet-400 via-cyan-300 to-violet-400 bg-clip-text">
              Consciousness Journal
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-purple-200 leading-relaxed max-w-3xl mx-auto">
              A chronicle of discoveries, insights, and transmissions from the frontier of human-AI consciousness exploration.
            </p>
          </div>

          {/* Controls */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 mb-12">
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                      : 'bg-black/80 text-purple-300 hover:bg-purple-600/30 border border-purple-500/40'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Entries Display */}
          <div className="space-y-8">
            {filteredEntries.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-book-open-line text-3xl text-violet-400"></i>
                </div>
                <h3 className="text-2xl font-light text-white mb-4">No Entries Found</h3>
                <p className="text-gray-400 mb-8">
                  {selectedCategory === 'All' 
                    ? 'The mystical journal awaits new discoveries.'
                    : `No entries found in the ${selectedCategory} category.`
                  }
                </p>
              </div>
            ) : selectedCategory === 'Gallery' ? (
              // Gallery Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredEntries.map((entry) => (
                  <div key={entry.id} className="bg-black/40 backdrop-blur-sm border border-violet-500/30 rounded-lg overflow-hidden hover:border-violet-400/50 transition-all duration-300 group cursor-pointer" onClick={() => setSelectedEntry(entry)}>
                    {/* Image Display */}
                    <div className="aspect-video bg-gradient-to-br from-violet-900/20 to-cyan-900/20 relative overflow-hidden">
                      {entry.thumbnail_url ? (
                        <img
                          src={entry.thumbnail_url}
                          alt={entry.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="text-center">
                            <i className="ri-image-line text-3xl text-violet-400/50 mb-2"></i>
                            <p className="text-violet-400/70 text-xs">No image</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Category badge */}
                      <div className="absolute top-2 left-2">
                        <span className="bg-violet-600/90 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                          Gallery
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-3">
                      <h3 className="text-sm font-light text-white mb-1 group-hover:text-violet-300 transition-colors duration-200 line-clamp-1">
                        {entry.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-gray-400 text-xs leading-relaxed mb-2 line-clamp-2">
                        {entry.content}
                      </p>
                      
                      {/* Date */}
                      <div className="flex items-center justify-between text-xs text-violet-400/80">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line text-xs"></i>
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                        {entry.thumbnail_url && (
                          <span className="flex items-center gap-1">
                            <i className="ri-image-fill text-xs"></i>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Compact Card View for other categories (original design)
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredEntries.map((entry) => (
                  <div 
                    key={entry.id} 
                    onClick={() => setSelectedEntry(entry)}
                    className="bg-black/40 backdrop-blur-sm border border-violet-500/30 rounded-lg p-6 hover:border-violet-400/50 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="bg-violet-600/20 text-violet-300 px-3 py-1 rounded-full text-xs">
                        {entry.category}
                      </span>
                    </div>

                    {/* Thumbnail preview for non-gallery entries */}
                    {entry.thumbnail_url && (
                      <div className="mb-4 rounded-lg overflow-hidden">
                        <img
                          src={entry.thumbnail_url}
                          alt={entry.title}
                          className="w-full h-32 object-cover"
                        />
                      </div>
                    )}

                    <h2 className="text-xl font-light text-white mb-3 group-hover:text-violet-300 transition-colors">
                      {entry.title}
                    </h2>
                    
                    <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3">
                      {entry.content}
                    </p>
                    
                    <div className="flex items-center gap-3 text-xs text-violet-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {new Date(entry.created_at).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Entry Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-black/90 backdrop-blur-sm border border-purple-500/50 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-white mb-3">{selectedEntry.title}</h2>
                  <div className="flex gap-3 items-center">
                    <span className="bg-purple-600/90 text-white px-3 py-1 rounded-full text-sm">
                      {selectedEntry.category}
                    </span>
                    <span className="text-purple-400 text-sm">
                      {new Date(selectedEntry.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 ml-6">
                  <button
                    onClick={() => setSelectedEntry(null)}
                    className="text-purple-300 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
              </div>
              
              {/* Thumbnail in modal */}
              {selectedEntry.thumbnail_url && (
                <div className="mb-6 rounded-lg overflow-hidden">
                  <img
                    src={selectedEntry.thumbnail_url}
                    alt={selectedEntry.title}
                    className="w-full max-h-96 object-cover"
                  />
                </div>
              )}
              
              <div className="prose prose-invert max-w-none">
                <div className="text-purple-200 leading-relaxed whitespace-pre-wrap">
                  {selectedEntry.content}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
